/**
 * Team-wide play star (favorite) toggle — Play Archive rows / cards, the Play
 * Entry Actions card and the Coaches Highlight Starred Plays card.
 *
 * Outline ☆ when unstarred, filled ★ (accent gold) when starred. It is a real
 * toggle button (`aria-pressed`) with a spelled-out label, so the state is
 * never colour-only. `onToggle` is omitted for read-only viewers: the star then
 * renders as a static indicator (a starred play still shows as starred).
 */
import { STAR_COPY } from '../lib/constants';

/**
 * Inline SVG star — never depends on the device having a symbol font (a
 * missing ☆ glyph would make the control invisible). Outline when off, filled
 * when on; `currentColor` so the state colour comes from the CSS token.
 */
export function StarIcon({ isFilled }: { isFilled: boolean }): JSX.Element {
  return (
    <svg
      className="star-icon"
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M12 2.6l2.9 6 6.5.9-4.7 4.6 1.1 6.5L12 17.5l-5.8 3.1 1.1-6.5L2.6 9.5l6.5-.9z"
        fill={isFilled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface StarButtonProps {
  isStarred: boolean;
  /** Play number, for the accessible name ("Star play 12"). */
  playNumber?: number | null;
  onToggle?: () => void;
  isBusy?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Show the word (and `#n`) next to the glyph (Play Entry Actions card). */
  showLabel?: boolean;
  /** Renders the toggle disabled with this explanation (e.g. nothing logged yet). */
  disabledReason?: string | null;
  /** Short visible label while `disabledReason` applies (with `showLabel`). */
  disabledLabel?: string | null;
  testId?: string;
}

export function StarButton({
  isStarred,
  playNumber = null,
  onToggle,
  isBusy = false,
  size = 'sm',
  showLabel = false,
  disabledReason = null,
  disabledLabel = null,
  testId,
}: StarButtonProps): JSX.Element {
  const subject = playNumber === null ? STAR_COPY.thisPlay : `${STAR_COPY.play} ${playNumber}`;
  const glyph = <StarIcon isFilled={isStarred} />;
  const isBlocked = disabledReason !== null;
  const className = `star-btn star-btn--${size}${isStarred ? ' star-btn--on' : ''}`;
  if (onToggle === undefined) {
    if (!isStarred) return <span className="star-btn star-btn--placeholder" aria-hidden="true" />;
    return (
      <span className={className} title={STAR_COPY.starredTitle} data-testid={testId}>
        <span className="star-btn__glyph" aria-hidden="true">
          {glyph}
        </span>
        <span className="visually-hidden">{STAR_COPY.starred}</span>
      </span>
    );
  }
  const label = isStarred ? `${STAR_COPY.unstar} ${subject}` : `${STAR_COPY.star} ${subject}`;
  return (
    <button
      type="button"
      className={className}
      aria-pressed={isStarred}
      aria-label={label}
      title={disabledReason ?? (isStarred ? STAR_COPY.unstarTitle : STAR_COPY.starTitle)}
      disabled={isBusy || isBlocked}
      data-testid={testId}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
    >
      <span className="star-btn__glyph" aria-hidden="true">
        {glyph}
      </span>
      {showLabel ? (
        <span className="star-btn__label">
          {isBlocked && disabledLabel !== null ? (
            disabledLabel
          ) : (
            <>
              {isStarred ? STAR_COPY.starred : STAR_COPY.star}
              {playNumber === null ? '' : ` #${playNumber}`}
            </>
          )}
        </span>
      ) : null}
    </button>
  );
}
